import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { orgContext } from '../services/api';
import { stopAllArmedAutoSyncs } from '../utils/localFolderSync/autoSyncRegistry';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOrg, setActiveOrgState] = useState(null);

  useEffect(() => {
    const storedToken = api.getToken();
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    // Restore org context
    const storedOrg = orgContext.getActiveOrg();
    if (storedOrg && orgContext.getToken()) {
      setActiveOrgState(storedOrg);
    }
    setLoading(false);
  }, []);

  // Fired by api.js's response interceptor on a 401 instead of it hard-
  // redirecting itself (see api.js's own comment) - this clears auth state
  // the same way a manual logout() would; App.js's SessionExpiredRedirect
  // (mounted inside <Router>, which this provider sits outside of) handles
  // the actual navigate('/auth') in response to the same event.
  useEffect(() => {
    const handleSessionExpired = () => logout();
    window.addEventListener('cm:session-expired', handleSessionExpired);
    return () => window.removeEventListener('cm:session-expired', handleSessionExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (data) => {
    try {
      api.setToken(data.token);
      localStorage.setItem('user', JSON.stringify({
        customer_id: data.customer_id,
        customer_name: data.customer_name,
        email: data.email
      }));
      setToken(data.token);
      setUser({
        customer_id: data.customer_id,
        customer_name: data.customer_name,
        email: data.email
      });
      // A folder auto-sync armed under a different signed-in account (or
      // anonymously) shouldn't keep uploading under the identity that just
      // logged in - see stopAllArmedAutoSyncs's own doc comment. Best-effort;
      // must never block a successful login.
      stopAllArmedAutoSyncs().catch(() => {});
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    api.clearToken();
    localStorage.removeItem('user');
    orgContext.clearToken();
    orgContext.clearActiveOrg();
    setToken(null);
    setUser(null);
    setActiveOrgState(null);
    // Same reasoning as login() above - the account that armed auto-sync is
    // no longer signed in, so it must not silently keep uploading as them.
    stopAllArmedAutoSyncs().catch(() => {});
  };

  const switchToOrg = async (orgId) => {
    const data = await api.switchOrgContext(orgId);
    orgContext.setToken(data.org_token);
    const org = {
      org_id: data.org_id,
      org_name: data.org_name,
      org_role: data.org_role,
      org_customer_id: data.org_customer_id,
    };
    orgContext.setActiveOrg(org);
    setActiveOrgState(org);
    return org;
  };

  const switchToPersonal = () => {
    orgContext.clearToken();
    orgContext.clearActiveOrg();
    setActiveOrgState(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token,
    // Org context
    activeOrg,
    isOrgContext: !!activeOrg,
    orgRole: activeOrg?.org_role || null,
    switchToOrg,
    switchToPersonal,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
